import { isDefined } from 'src/util/isDefined'
import { isNonEmptyString } from 'src/util/isNonEmptyString'

type Link = `https://${string}`

export type Campaign = {
  readonly campaignId: string
  readonly title: string
  /**
   * 行動を促すボタンのラベル（e.g. 詳細はこちら）
   */
  readonly cta: string
  readonly backgroundColor: string
  readonly foregroundColor: string
  readonly link: Link
  readonly imageUrl: {
    readonly large: string
    readonly small: string
  }
}

// MEMO: zodなどのスキーマバリデーション系のライブラリ入れたい気持ちにはかられる。

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * campaign_id は Cookie の値としてそのまま書き込むため、
 * Cookie の構文に影響する文字（カンマ・セミコロン・空白など）が混ざらないことを保証する。
 */
const isCampaignId = (value: unknown): value is string =>
  isNonEmptyString(value) && /^[\w-]+$/.test(value)

const isHexColor = (value: unknown): value is string =>
  isNonEmptyString(value) && /^#[0-9a-fA-F]{6}$/.test(value)

/**
 * link はキャンペーン管理側が入力した文字列がそのまま届くため、
 * href に渡す前に https のみに限定する。
 */
const isHttpsUrl = (value: unknown): value is Link => {
  if (!isNonEmptyString(value)) return false
  try {
    return new URL(value).protocol === 'https:'
  } catch {
    return false
  }
}

const parseCampaign = (value: unknown): Campaign | undefined => {
  if (!isRecord(value)) return undefined

  const {
    campaign_id: campaignId,
    title,
    cta,
    background_color: backgroundColor,
    foreground_color: foregroundColor,
    link,
    image_url: imageUrl,
  } = value

  if (!isCampaignId(campaignId)) return undefined
  if (!isNonEmptyString(title)) return undefined
  if (!isNonEmptyString(cta)) return undefined
  if (!isHexColor(backgroundColor)) return undefined
  if (!isHexColor(foregroundColor)) return undefined
  if (!isHttpsUrl(link)) return undefined
  if (!isRecord(imageUrl)) return undefined
  if (!isNonEmptyString(imageUrl['large'])) return undefined
  if (!isNonEmptyString(imageUrl['small'])) return undefined

  return {
    campaignId,
    title,
    cta,
    backgroundColor,
    foregroundColor,
    link,
    imageUrl: { large: imageUrl['large'], small: imageUrl['small'] },
  }
}

/**
 * キャンペーン API のレスポンスを画面で扱える形に変換する。
 *
 * バナーは業務に必須の情報ではないため、想定と違う形のデータが届いた場合は
 * その要素を捨てて「バナーが出ない」状態に倒す。
 * 流石にフォーマットが間違っているとかをわざわざHaramiのログに出す必要はないと思うので、捨てるのみ。
 */
export const parseCampaigns = (value: unknown): readonly Campaign[] => {
  if (!Array.isArray(value)) return []

  return value.map(parseCampaign).filter(isDefined)
}
